// Smartify BLE GATT protocol — a direct port of the Flutter ble_config.dart.
//
// THIS IS THE ONLY FILE THAT ENCODES THE FIRMWARE CONTRACT. Everything else in
// core/ble is written generically against these constants, exactly as the Dart
// layer is, so a firmware change is edited here and nowhere else.
//
// The ESP32 exposes ONE service with ONE characteristic that carries every role:
// Wi-Fi provisioning writes, control writes, and all notifications.

// Devices advertise "Smartify" + UniqueNumber, e.g. "Smartify083A8D406BE4".
// The UniqueNumber is the MAC as uppercase hex with no separators — the SAME
// identifier used for MQTT topics and every backend call, so a device is one
// identity across all transports.
export const BLE_NAME_PREFIX = 'Smartify';

// Firmware SERVICE_UUID 0x00FF, in 128-bit base form.
export const SERVICE_UUID = '000000ff-0000-1000-8000-00805f9b34fb';

// Firmware CHARACTERISTIC_UUID 0xFF01 — READ | WRITE | WRITE_NR | NOTIFY.
export const CHAR_UUID = '0000ff01-0000-1000-8000-00805f9b34fb';

// Phase 2: firmware OTA image transfer. Reserved here so nothing else claims it.
export const OTA_CHAR_UUID = '0000ff02-0000-1000-8000-00805f9b34fb';

// ⚠️ MTU — THE MOST COMMON INTEGRATION FAILURE, per the spec, and it fails
// SILENTLY. The default MTU of 23 leaves 20 bytes of payload, and the messages
// that matter are longer than that:
//
//     AUTH:<32 hex>        37 bytes
//     WIFI:<ssid>,<pass>   up to 101
//     CLAIM:…              up to ~130
//
// Without negotiation those writes are truncated with no error, so auth simply
// never succeeds and provisioning never parses. Firmware reassembles chunked
// writes on a 2-second idle timer as a safety net; the spec says not to rely on
// it, and neither do we.
export const REQUESTED_MTU = 247;

// The name lives in the SCAN RESPONSE, not the advertisement — 31 bytes will not
// hold both it and the service data. A PASSIVE scan therefore sees the service
// UUID and no name at all, which reads as "found nothing recognisable".
export const REQUIRE_ACTIVE_SCAN = true;

// ESP32 GATT servers commonly accept the first write-WITH-response then stall on
// the next — which presents as "the command only works once". Write-without-
// response avoids that, and is what the Dart uses for the same reason.
export const WRITE_WITHOUT_RESPONSE = true;

export const SCAN_TIMEOUT_MS = 15000;
export const CONNECT_TIMEOUT_MS = 20000;

// ── Payload builders ───────────────────────────────────────────────────────
// These match the ESP32 write handler byte for byte.

// The firmware's parser (`parse_addwifi_custom`) prefers '&' as the separator:
// an SSID may legally contain commas and spaces, but not '&'.
//
// ⚠️ EXACT LITERAL — no case or spacing changes, and NOT the same as the MQTT
// form. Over MQTT the fields run together with no separators at all
// (`ADDWIFIssid=…pass=…`, see core/devices/commands.js); over BLE there is a
// space after ADDWIFI and an '&' between the fields. Swapping one for the other
// parses as nothing.
export const buildWifiCredentials = (ssid, password) =>
  `ADDWIFI ssid=${ssid}&pass=${password}`;

// The other accepted form. Simpler, but comma-delimited.
export const buildWifiColon = (ssid, password) => `WIFI:${ssid},${password}`;

// Firmware limits. Longer values are silently truncated rather than rejected,
// so they are checked before sending.
//
// ⚠️ ONE LESS THAN THE FIRMWARE'S NUMBERS, AND DELIBERATELY. wifi_manager.h
// declares `MAX_SSID_LEN 32` / `MAX_PASS_LEN 64`, but those are BUFFER sizes —
// `char ssid[MAX_SSID_LEN]` — so the usable string is one shorter once the null
// terminator is counted. Raising these to 32/64 to "match the firmware" would
// let a full-length password through and have its last character eaten by
// strlcpy, giving a device that saves credentials and cannot join.
export const MAX_SSID_LEN = 31;
export const MAX_PASS_LEN = 63;

/**
 * Pick the Wi-Fi payload that survives THESE credentials.
 *
 * ⚠️ `WIFI:` IS THE DEFAULT, and it did not used to be. The confirmed BLE
 * command list carries `WIFI:<ssid>,<pass>` and nothing else, so that is what a
 * current device is known to parse; `ADDWIFI ssid=..&pass=..` stays only as the
 * fallback for the one case `WIFI:` cannot express. Preferring ADDWIFI, as this
 * did, risks a device that accepts the write, parses nothing, and never joins —
 * silent at both ends.
 *
 * Neither form is universal, and the failure is a parse error at the device
 * rather than anything visible here:
 *   `WIFI:<ssid>,<pass>`             breaks on a COMMA in the password
 *   `ADDWIFI ssid=..&pass=..`        breaks on an '&' in the SSID
 *
 * Credentials that would break both cannot be sent over BLE at all, and saying
 * so plainly beats a device that silently never joins.
 *
 * -> { ok, payload } | { ok: false, error }
 */
export function chooseWifiPayload(ssid, password) {
  const s = String(ssid ?? '');
  const p = String(password ?? '');

  if (!s) return { ok: false, error: 'Wi-Fi name cannot be empty.' };
  if (s.length > MAX_SSID_LEN) {
    return { ok: false, error: `Wi-Fi name is too long (max ${MAX_SSID_LEN} characters).` };
  }
  if (p.length > MAX_PASS_LEN) {
    return { ok: false, error: `Wi-Fi password is too long (max ${MAX_PASS_LEN} characters).` };
  }

  // ⚠️ ONLY THE SSID DECIDES. Read from the firmware's own
  // `parse_addwifi_custom` (wifi_manager.c) rather than guessed:
  //
  //   no "ssid=" marker  -> split on the FIRST ',', else the first ':', else
  //                         the first ' '. We always send a comma, so the comma
  //                         wins and everything after it is the password —
  //                         commas, colons, spaces and '&' included.
  //   "ssid=" present    -> SSID runs to the first '&', password is everything
  //                         after "pass=".
  //
  // So `WIFI:` breaks on a comma IN THE SSID, and `ADDWIFI` breaks on an '&' IN
  // THE SSID. The PASSWORD cannot break either form.
  //
  // This previously refused any password containing a comma, on the assumption
  // that the split might be greedy. It is not — and that caution rejected
  // perfectly sendable credentials, e.g. SSID "Tom & Jerry" with password
  // "a,b", which `WIFI:` carries correctly.
  const ssidHasAmp = s.includes('&');
  const ssidHasComma = s.includes(',');

  if (ssidHasComma && ssidHasAmp) {
    return {
      ok: false,
      error: 'This network name cannot be sent over Bluetooth: it contains both '
        + '"," and "&", and each format uses one of them as its separator. '
        + 'Set it up over Wi-Fi instead.',
    };
  }
  return {
    ok: true,
    payload: ssidHasComma ? buildWifiCredentials(s, p) : buildWifiColon(s, p),
  };
}

// ── Commands (§4) ──────────────────────────────────────────────────────────
// The SAME strings the app already sends over MQTT. There is no second
// vocabulary — only a second transport.
export const CMD_AUTH = 'AUTH';
export const CMD_PING = 'PING';
export const CMD_STATUS = 'STATUS';
export const CMD_INFO = 'INFO';
export const CMD_WHOAMI = 'WHOAMI';

export const buildAuthAnswer = (response) => `AUTH:${response}`;
export const buildOwnerAnswer = (response) => `OWNER:${response}`;

// ── Advertised service data (§2) ───────────────────────────────────────────
// <state:1><owner_tag:4>, letting the scan list decide what to show WITHOUT
// connecting:
//   state 0x00            unassigned — claimable, "new device"
//   state 0x01 + my tag   mine
//   state 0x01 + other    someone else's — hide
//
// A display filter, not a security control: a BLE device cannot hide from a
// determined scanner. The real protection is that an assigned device refuses
// commands without owner authentication.
//
// ⚠️ NO key_version FIELD. §0 of the spec says the device "stores a key_version
// byte and advertises it", but this layout is the five bytes above and has
// nowhere to put it. Raised with the firmware side; until it is resolved the app
// pins DEFAULT_KEY_VERSION (see bleAuth).
export const STATE_UNASSIGNED = 0x00;
export const STATE_ASSIGNED = 0x01;

export function parseServiceData(dataView) {
  try {
    const b = new Uint8Array(dataView.buffer ?? dataView);
    if (b.length < 1) return null;
    const state = b[0];
    const ownerTag = b.length >= 5
      ? Array.from(b.slice(1, 5)).map((x) => x.toString(16).padStart(2, '0').toUpperCase()).join('')
      : null;
    return { state, ownerTag, assigned: state === STATE_ASSIGNED };
  } catch (e) {
    return null;
  }
}

// Relay numbers are 1-based; firmware parses "%d-%d".
export const buildRelayCommand = (relay, on) => `${relay}-${on ? 1 : 0}`;

// ⚠️ `ALLON` / `ALLOFF`, NO SPACE. This used to send "all on"/"all off" on a
// note that the firmware matched both spellings. The confirmed BLE command list
// carries only the unspaced form, and it is the same word the MQTT path already
// uses (core/devices/commands.js CMD_ALL_ON). A command the parser does not
// recognise is not an error at either end — the relays simply do not move.
export const buildAllRelays = (on) => (on ? 'ALLON' : 'ALLOFF');

// ── Notification parsing ───────────────────────────────────────────────────
// Firmware notifies plain strings:
//   success  "WIFI SAVED ssid=<ssid> rebooting"   (device reboots, link drops)
//   parse    "WIFI PARSE FAILED"
//   save err "WIFI SAVE ERROR=<esp_err_name>"

export const isProvisionSuccess = (text) =>
  String(text || '').toUpperCase().includes('SAVED');

export const isProvisionFailure = (text) => {
  const u = String(text || '').toUpperCase();
  return u.includes('PARSE FAILED') || u.includes('ERROR');
};

// DataView <-> string helpers. The plugin hands notifications back as DataView.
export function decodeNotification(dataView) {
  try {
    const bytes = new Uint8Array(dataView.buffer ?? dataView);
    return new TextDecoder().decode(bytes).trim();
  } catch (e) {
    return '';
  }
}

export function encode(text) {
  const bytes = new TextEncoder().encode(text);
  return new DataView(bytes.buffer);
}
